// Shared team-color helpers (#286). The team-level color lives on the team
// (set via team/view "Set Team Color"); these utilities render it legibly
// wherever it surfaces. Kept in _shared so team/view, the color modal, and
// future consumers (player pages once the color is projected into their
// payloads) reuse one contrast rule instead of each re-deriving it.

const HEX_RE = /^#[0-9a-fA-F]{6}$/

export function isHexColor(value: string | null | undefined): value is string {
  return typeof value === 'string' && HEX_RE.test(value)
}

/**
 * Pick a readable text color (light or dark) for a given background hex, using
 * a perceived-luminance (YIQ) threshold. Dark backgrounds -> white text; light
 * backgrounds -> near-black slate. Falls back to white for malformed input.
 * (Mirrors the dashboard's `autoContrastText`; duplicated here deliberately to
 * avoid a polish-stream feature importing from the dashboard feature.)
 */
export function autoContrastText(backgroundHex: string): string {
  const h = backgroundHex.replace('#', '')
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return '#ffffff'
  const r = parseInt(h.slice(0, 2), 16)
  const g = parseInt(h.slice(2, 4), 16)
  const b = parseInt(h.slice(4, 6), 16)
  const yiq = (r * 299 + g * 587 + b * 114) / 1000
  return yiq >= 150 ? '#0f172a' : '#ffffff'
}

// ---------------------------------------------------------------------------
// headerColors — team-colored header crest (player card, color-picker preview)
// ---------------------------------------------------------------------------

export const NAVY_TEAM_COLOR = '#113a57'
// Shared fallback slab for surfaces that fill a team's whole row/box with color
// (e.g. the playoffs bracket + stripe banner) when the team never chose one — and
// for all pre-#496 seasons (no backfill). A soft light cream so an unset team
// reads as a deliberate "no color chosen" neutral rather than dead gray, carried
// identically whether it wins or loses.
export const DEFAULT_TEAM_SLAB = '#f3ebd8' // light cream (lighter / away)
// Deeper companion shade for surfaces that STACK two unset teams (the playoffs
// bracket): away takes the lighter slab, home the deeper one, so the two
// opponents read as distinct slabs instead of one flat block. This is positional
// (by slot), NOT a fabricated per-team identity — an unset team still reads
// neutral everywhere else. Both shades stay light enough for dark auto-contrast
// text, so type color is consistent across the two rows.
export const DEFAULT_TEAM_SLAB_HOME = '#e6d7b4' // deeper cream / tan (deeper / home)
const DARK_TEXT = '#0f172a'
const LIGHT_TEXT = '#ffffff'

function parseRgb(hex: string | null | undefined): [number, number, number] | null {
  if (!hex) return null
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim())
  if (!m) return null
  const n = parseInt(m[1], 16)
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
}

// WCAG relative luminance (0 = black, 1 = white).
function wcagLuminance([r, g, b]: [number, number, number]): number {
  const f = (c: number) => {
    const s = c / 255
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4)
  }
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b)
}

export interface HeaderColors { bg: string; fg: string; border: string; fgIsDark: boolean }

// Note: headerColors uses WCAG relative luminance (not the YIQ approximation in
// autoContrastText above). Both pick between dark/light text — WCAG is more
// accurate for saturated mid-range colors (e.g. red #ef4444).
/**
 * Crest colors for a team-colored header: primary = field (bg), secondary =
 * border/stripe accent (falls back to bg when unset), foreground auto-contrasts.
 * Single source of truth shared by the player card and the color-picker preview.
 */
export function headerColors(
  primary: string | null | undefined,
  secondary: string | null | undefined,
): HeaderColors {
  const toHex = (rgb: [number, number, number]) =>
    `#${rgb.map((c) => c.toString(16).padStart(2, '0')).join('')}`
  const rgb = parseRgb(primary)
  const sRgb = parseRgb(secondary)
  if (!rgb) {
    // No primary chosen → navy default field, but still honor a lone secondary as
    // the accent (stripe / border / position-badge patch) so a team that set only
    // a secondary color isn't rendered blank (the picker lets you save one alone).
    return { bg: NAVY_TEAM_COLOR, fg: LIGHT_TEXT, border: sRgb ? toHex(sRgb) : NAVY_TEAM_COLOR, fgIsDark: false }
  }
  const bg = toHex(rgb)
  const fg = wcagLuminance(rgb) > 0.179 ? DARK_TEXT : LIGHT_TEXT // 0.179 = WCAG crossover; above => dark text reads better
  const border = sRgb ? toHex(sRgb) : bg
  return { bg, fg, border, fgIsDark: fg === DARK_TEXT }
}
